import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Clock } from 'lucide-react';
import SportsScorerPage from './SportsScorerPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A court's permanent scorer QR (printed once, left on the court). It opens the
// match live on that court right now, and moves on to the next one by itself —
// the old poster encoded ONE match and went stale after a single game.
export default function SportsCourtScorerPage() {
  const { courtToken } = useParams();
  const [court, setCourt] = useState(null); // { qr_token, court_name, session_name, session_status }
  const [error, setError] = useState('');
  const [finished, setFinished] = useState(false);

  const resolve = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/sports/open-play/score/court/${courtToken}`);
      setCourt(prev => (prev && prev.qr_token === r.data.qr_token && prev.session_status === r.data.session_status
        && prev.court_paused === r.data.court_paused ? prev : r.data));
      setError('');
    } catch (e) {
      setError(e.response?.status === 404 ? 'This court QR is no longer valid — ask the organizer for the current one.'
        : 'Can’t reach the server — retrying…');
    }
  }, [courtToken]);

  useEffect(() => { resolve(); }, [resolve]);
  // A new match on this court resets the scorer.
  useEffect(() => { setFinished(false); }, [court?.qr_token]);
  // Keep looking: often while nothing is live here or the shown match ended;
  // slower while a match is live — the coordinator may pause the court and
  // send that game to another court (7.4), and this phone must let go of it.
  const live = court?.qr_token && court.session_status === 'active';
  useEffect(() => {
    const iv = setInterval(resolve, live && !finished ? 10000 : 5000);
    return () => clearInterval(iv);
  }, [live, finished, resolve]);

  if (court?.qr_token) {
    return <SportsScorerPage key={court.qr_token} qrTokenOverride={court.qr_token}
      onFinishedChange={setFinished} courtMode />;
  }
  return (
    <div data-testid="court-scorer-waiting" className="min-h-screen flex flex-col items-center justify-center p-8 text-center"
      style={{ background: '#0A0A0A', color: '#fff' }}>
      <Clock className="h-10 w-10 mb-4" style={{ color: '#F59E0B' }} />
      {error ? (
        <p className="text-sm" style={{ color: '#FFB4B4' }}>{error}</p>
      ) : !court ? (
        <p className="text-sm" style={{ color: '#737373' }}>Loading court…</p>
      ) : (
        <>
          <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>{court.court_name || 'Court'}</h2>
          <p className="text-xs mb-4" style={{ color: '#737373' }}>{court.session_name}</p>
          <p className="text-sm" style={{ color: '#A3A3A3' }}>
            {court.session_status === 'active' && court.court_paused
              ? 'This court is paused — no open play matches here for now. Scoring opens again when the organizer reopens it.'
              : court.session_status === 'active'
              ? 'No match on this court right now. The next one opens here automatically.'
              : court.session_status === 'ended' ? 'This session has ended.' : 'Scoring opens once the session starts.'}
          </p>
        </>
      )}
    </div>
  );
}
